import { forwardRef, type TextareaHTMLAttributes } from 'react';
import { cn } from '@/lib/utils';

export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  error?: string;
  helperText?: string;
  compact?: boolean;
}

const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ className, label, error, helperText, id, compact = false, ...props }, ref) => {
    const textareaId = id || label?.toLowerCase().replace(/\s+/g, '-');

    return (
      <div className="w-full">
        {label && (
          <label
            htmlFor={textareaId}
            className={cn(
              'block font-medium',
              'text-gray-700 dark:text-secondary-300',
              compact ? 'mb-1 text-xs' : 'mb-1.5 text-sm'
            )}
          >
            {label}
          </label>
        )}
        <textarea
          ref={ref}
          id={textareaId}
          className={cn(
            'flex w-full rounded-lg border text-sm transition-all duration-200',
            'placeholder:text-gray-400 dark:placeholder:text-secondary-500',
            'focus:outline-none focus:ring-2 focus:ring-offset-0',
            'disabled:cursor-not-allowed disabled:opacity-50',
            'resize-y',
            // Size
            compact ? 'min-h-[80px] px-2.5 py-1.5' : 'min-h-[100px] px-3 py-2',
            // Light mode
            'bg-white border-gray-300',
            'focus:border-primary-500 focus:ring-primary-500/20',
            'disabled:bg-gray-50',
            // Dark mode
            'dark:bg-secondary-900/50 dark:border-white/[0.1]',
            'dark:text-secondary-100',
            'dark:focus:border-primary-500/50 dark:focus:ring-primary-500/20',
            'dark:focus:bg-secondary-900/80',
            'dark:disabled:bg-secondary-900/30',
            // Error state
            error && cn(
              'border-danger-500 focus:border-danger-500 focus:ring-danger-500/20',
              'dark:border-danger-500/50 dark:focus:border-danger-500/70'
            ),
            className
          )}
          {...props}
        />
        {error && (
          <p className={cn('text-danger-600 dark:text-danger-400', compact ? 'mt-1 text-xs' : 'mt-1.5 text-sm')}>
            {error}
          </p>
        )}
        {helperText && !error && (
          <p className={cn('text-gray-500 dark:text-secondary-500', compact ? 'mt-1 text-xs' : 'mt-1.5 text-sm')}>
            {helperText}
          </p>
        )}
      </div>
    );
  }
);

Textarea.displayName = 'Textarea';

export { Textarea };
